import type * as React from "react";
import { Skeleton } from "@/components/ui/atoms/skeleton";
import { cn } from "@/lib/utils";

// ─── SuggestionCard ──────────────────────────────────────────────────────────

export type SuggestionCardProps = {
  /** Icon element rendered at the top of the card */
  icon: React.ReactNode;
  /** Primary label text */
  title: string;
  /** Secondary descriptive text */
  subtitle: string;
} & React.ComponentProps<"button">;

export function SuggestionCard({
  className,
  icon,
  title,
  subtitle,
  style,
  ...props
}: SuggestionCardProps) {
  return (
    <button
      className={cn(
        "flex w-full cursor-pointer flex-col gap-1.5 rounded-2xl border bg-card text-left shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      data-slot="suggestion-card"
      style={{
        padding: "clamp(0.75rem, 1.6vh, 1rem)",
        ...style,
      }}
      type="button"
      {...props}
    >
      <div
        className="flex items-center justify-center rounded-xl border bg-muted shadow-xs"
        data-slot="suggestion-card-icon"
        style={{
          width: "clamp(2rem, 2.8vh, 2.5rem)",
          height: "clamp(2rem, 2.8vh, 2.5rem)",
        }}
      >
        {icon}
      </div>
      <div
        className="space-y-1.5"
        style={{ paddingTop: "clamp(0.25rem, 0.8vh, 0.5rem)" }}
      >
        <h3
          className="font-semibold text-card-foreground text-sm leading-tight"
          data-slot="suggestion-card-title"
        >
          {title}
        </h3>
        <p
          className="text-muted-foreground text-xs leading-snug"
          data-slot="suggestion-card-subtitle"
        >
          {subtitle}
        </p>
      </div>
    </button>
  );
}

// ─── SuggestionCardSkeleton ─────────────────────────────────────────────────

export type SuggestionCardSkeletonProps = React.ComponentProps<"div">;

export function SuggestionCardSkeleton({
  className,
  style,
  ...props
}: SuggestionCardSkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex w-full flex-col gap-1.5 rounded-2xl border bg-card shadow-xs",
        className
      )}
      data-slot="suggestion-card-skeleton"
      style={{
        padding: "clamp(0.75rem, 1.6vh, 1rem)",
        ...style,
      }}
      {...props}
    >
      <Skeleton
        className="rounded-xl"
        style={{
          width: "clamp(2rem, 2.8vh, 2.5rem)",
          height: "clamp(2rem, 2.8vh, 2.5rem)",
        }}
      />
      <div
        className="space-y-1.5"
        style={{ paddingTop: "clamp(0.25rem, 0.8vh, 0.5rem)" }}
      >
        <Skeleton className="h-4 w-3/4 rounded" />
        <Skeleton className="h-3 w-1/2 rounded" />
      </div>
    </div>
  );
}
